/**
 * 업체 관리 목록.
 * `GET /admin/partner-companies` 응답의 summary + page를 그대로 사용한다.
 */
import { type PartnerCompanyContractStatus } from '@/entities/partner-company/api/partner-company.api';
import { usePartnerCompanyList } from '@/entities/partner-company/hooks/usePartnerCompanies';
import { DataCard } from '@/shared/components/DataCard';
import { DateRangePicker } from '@/shared/components/DatePicker';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  ROUTE_PATHS,
  partnerCompanyDetailPath,
} from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { formatDate } from '@/shared/lib/dateFormat';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Briefcase, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  keyword: '',
  contractStatus: 'all',
  contractEndFrom: '',
  contractEndTo: '',
};

const CONTRACT_STATUS_OPTIONS: Array<{
  value: 'all' | PartnerCompanyContractStatus;
  label: string;
}> = [
  { value: 'all', label: '전체' },
  { value: 'ACTIVE', label: '계약중' },
  { value: 'NOT_CONTRACTED', label: '계약전' },
  { value: 'RENEWAL_DUE', label: '만료임박' },
  { value: 'TERMINATED', label: '해지' },
];

export const PartnerCompanyPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { values, setValues } = useListSearchParams(SEARCH_DEFAULTS);
  const [keywordInput, setKeywordInput] = useState(values.keyword);

  useEffect(() => {
    setKeywordInput(values.keyword);
  }, [values.keyword]);

  const search = useMemo(
    () => ({
      keyword: values.keyword || undefined,
      contractStatus:
        values.contractStatus && values.contractStatus !== 'all'
          ? (values.contractStatus as PartnerCompanyContractStatus)
          : undefined,
      contractEndFrom: values.contractEndFrom || undefined,
      contractEndTo: values.contractEndTo || undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = usePartnerCompanyList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [error, isError, toast]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({ keyword: keywordInput.trim(), page: 1 });
  };

  const rows = data?.page.content ?? [];
  const totalCount = data?.page.totalCount ?? 0;
  const stats = data?.summary;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Briefcase}
        title="재위탁 업체 관리"
        description="업체 정보를 조회하고 등록합니다"
        actions={
          <MenuWriteGate>
            <Button
              onClick={() =>
                navigate({ to: ROUTE_PATHS.PARTNER_COMPANY_NEW } as never)
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              업체 등록
            </Button>
          </MenuWriteGate>
        }
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="flex flex-col gap-1">
            <Label htmlFor="partner-company-keyword">업체명 / 사업자번호</Label>
            <Input
              id="partner-company-keyword"
              placeholder="업체명 또는 사업자등록번호"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              className="w-64"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>계약상태</Label>
            <Select
              value={values.contractStatus || 'all'}
              onValueChange={(contractStatus) =>
                setValues({ contractStatus, page: 1 })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="전체" />
              </SelectTrigger>
              <SelectContent>
                {CONTRACT_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label>계약 기간</Label>
            <DateRangePicker
              className="w-64"
              value={{
                from: values.contractEndFrom,
                to: values.contractEndTo,
              }}
              onChange={({ from, to }) =>
                setValues({
                  contractEndFrom: from,
                  contractEndTo: to,
                  page: 1,
                })
              }
              placeholder="날짜 범위 선택"
            />
          </div>
          <Button type="submit" className="ruda-button">
            검색
          </Button>
        </FilterRow>
      </FilterCard>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="전체" value={stats?.total ?? 0} />
        <StatTile label="계약중" value={stats?.active ?? 0} />
        <StatTile label="만료임박" value={stats?.renewalDue ?? 0} />
        <StatTile label="해지" value={stats?.terminated ?? 0} />
      </div>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="업체 목록을 불러오는 중..." />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            description={
              values.keyword ||
              values.contractStatus !== 'all' ||
              values.contractEndFrom ||
              values.contractEndTo
                ? '검색 조건에 맞는 업체가 없습니다.'
                : '등록된 업체가 없습니다.'
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>업체명</TableHead>
                  <TableHead>사업자번호</TableHead>
                  <TableHead>대표자</TableHead>
                  <TableHead>판촉영업 신고번호</TableHead>
                  <TableHead>계약기간</TableHead>
                  <TableHead>사용자</TableHead>
                  <TableHead>거래처</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((company) => (
                  <TableRow
                    key={company.partnerCompanyId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: partnerCompanyDetailPath(company.partnerCompanyId),
                      } as never)
                    }
                  >
                    <TableCell className="font-medium">
                      {company.companyName}
                    </TableCell>
                    <TableCell>{company.businessRegistrationNumber}</TableCell>
                    <TableCell>{company.representativeName}</TableCell>
                    <TableCell>{company.csoReportNumber || '-'}</TableCell>
                    <TableCell>
                      {formatContractPeriod(
                        company.contractStartDate,
                        company.contractEndDate,
                      )}
                    </TableCell>
                    <TableCell>{formatUserCount(company.userCount)}</TableCell>
                    <TableCell>
                      {formatClientCount(company.businessRelationshipCount)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getContractStatusBadgeVariant(
                          company.contractStatus,
                        )}
                      >
                        {getContractStatusLabel(company.contractStatus)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate({
                            to: partnerCompanyDetailPath(company.partnerCompanyId),
                          } as never);
                        }}
                      >
                        상세
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};

const getContractStatusLabel = (status?: PartnerCompanyContractStatus | null) => {
  if (status === 'ACTIVE') return '계약중';
  if (status === 'NOT_CONTRACTED') return '계약전';
  if (status === 'RENEWAL_DUE') return '만료임박';
  if (status === 'TERMINATED') return '해지';
  return '-';
};

const getContractStatusBadgeVariant = (
  status?: PartnerCompanyContractStatus | null,
) => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'NOT_CONTRACTED') return 'secondary';
  if (status === 'RENEWAL_DUE') return 'warning';
  if (status === 'TERMINATED') return 'destructive';
  return 'secondary';
};

const formatContractPeriod = (
  startDate?: string | null,
  endDate?: string | null,
) => {
  const start = startDate ? formatDate(startDate) : '';
  const end = endDate ? formatDate(endDate) : '';
  if ((!start || start === '-') && (!end || end === '-')) return '-';
  if (!start || start === '-') return `~ ${end}`;
  if (!end || end === '-') return `${start} ~`;
  return `${start} ~ ${end}`;
};

const formatUserCount = (count?: number | null) =>
  count == null ? '-' : `${count}명`;

/** 목록 거래처 열 — API `businessRelationshipCount` */
const formatClientCount = (count?: number | null) =>
  count == null ? '-' : String(count);

const StatTile = ({ label, value }: { label: string; value: number }) => (
  <Card size="md" className="px-5 py-4">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
  </Card>
);
